import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { NodeType, type CanvasNode, type ViewportTransform } from '../types';
import { TONE_COLORS, nodeTone } from './nodes/nodeTheme';
import {
    centeredOn, fromMap, mapFrame, nodeRect, nodesBounds, toMap, unionBounds, viewBounds,
    type Bounds, type MapFrame,
} from '../utils/canvasView';

const MAP_WIDTH = 208;
const MAP_HEIGHT = 136;
/** A press that moves less than this is a click. */
const CLICK_SLOP = 3;
/** The outline of the box that shows what is on screen. */
const VIEW_BOX_LINE = 1.25;
/**
 * Where the map stands: beside the zoom controls (16px from the edge, 42px wide, 8px between),
 * and with them left of a drawer open on the right (--canvas-right-inset, set on the page by the app).
 */
const MAP_RIGHT = 'calc(66px + var(--canvas-right-inset, 0px))';

/**
 * Where the box for what is on screen goes on the map, in the map's own pixels: at least 2 by 2,
 * and with its outline centred on the edge of the view (so half of it lies outside).
 */
export const viewBoxOnMap = (frame: MapFrame, t: ViewportTransform, viewWidth: number, viewHeight: number) => {
    const view = viewBounds(t, viewWidth, viewHeight);
    const a = toMap(frame, view.minX, view.minY);
    const b = toMap(frame, view.maxX, view.maxY);
    return {
        x: a.x - VIEW_BOX_LINE / 2,
        y: a.y - VIEW_BOX_LINE / 2,
        width: Math.max(2, b.x - a.x) + VIEW_BOX_LINE,
        height: Math.max(2, b.y - a.y) + VIEW_BOX_LINE,
    };
};


interface CanvasMinimapProps {
    nodes: CanvasNode[];
    selectedIds: Set<string>;
    /** The view as last committed; while a gesture runs, `subscribe` reports it live. */
    transform: ViewportTransform;
    /** The canvas's size on screen. */
    viewSize: { width: number; height: number };
    subscribe: (listener: (t: ViewportTransform) => void) => () => void;
    getLive: () => ViewportTransform;
    /** Puts the view there at once; `commit` at the end of a drag. */
    onMove: (t: ViewportTransform, commit: boolean) => void;
    /** Glides the view there. */
    onGlide: (t: ViewportTransform) => void;
    onHide: () => void;
}

/**
 * A small map of the whole workspace: every node, and a box for what is on screen. Click to go
 * somewhere, drag to move the view, click a node to bring it to the middle.
 */
export const CanvasMinimap: React.FC<CanvasMinimapProps> = ({
    nodes, selectedIds, transform, viewSize, subscribe, getLive, onMove, onGlide, onHide,
}) => {
    const viewBoxRef = useRef<HTMLDivElement>(null);
    // While dragging, the map holds still; otherwise it fits the nodes and the view
    const [frozen, setFrozen] = useState<MapFrame | null>(null);
    const press = useRef<{ pointerId: number; x: number; y: number; moved: boolean; nodeId?: string; grab: { dx: number; dy: number }; frame: MapFrame } | null>(null);

    const contentBounds = useMemo(() => nodesBounds(nodes), [nodes]);
    const liveFrame = useMemo(() => {
        const bounds: Bounds = unionBounds(contentBounds, viewBounds(transform, viewSize.width, viewSize.height));
        return mapFrame(bounds, MAP_WIDTH, MAP_HEIGHT);
    }, [contentBounds, transform, viewSize.width, viewSize.height]);
    const frame = frozen ?? liveFrame;
    const frameRef = useRef(frame);
    frameRef.current = frame;

    // The view box follows every change of view, also mid-gesture, without a render. It is an
    // HTML box on a layer of its own, moved by a transform: during a pan nothing on the map is
    // laid out or painted again (a shape inside the SVG was, on every frame). Its size only
    // changes with the zoom.
    useEffect(() => {
        let width = -1;
        let height = -1;
        const place = (t: ViewportTransform) => {
            const el = viewBoxRef.current;
            if (!el) return;
            const box = viewBoxOnMap(frameRef.current, t, viewSize.width, viewSize.height);
            // On whole screen pixels, so the layer is not blurred by being drawn between them
            const dpr = window.devicePixelRatio || 1;
            el.style.transform = `translate(${Math.round(box.x * dpr) / dpr}px, ${Math.round(box.y * dpr) / dpr}px)`;
            if (Math.abs(box.width - width) > 0.01) { width = box.width; el.style.width = `${width}px`; }
            if (Math.abs(box.height - height) > 0.01) { height = box.height; el.style.height = `${height}px`; }
        };
        place(getLive());
        return subscribe(place);
    }, [subscribe, getLive, viewSize.width, viewSize.height, frame]);

    const pointAt = (e: React.PointerEvent, f: MapFrame) => {
        const box = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
        // The map's own pixels, whatever size it is drawn at
        const mx = ((e.clientX - box.left) / box.width) * MAP_WIDTH;
        const my = ((e.clientY - box.top) / box.height) * MAP_HEIGHT;
        return fromMap(f, mx, my);
    };

    const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation(); // not a pan or a selection box on the canvas underneath
        const live = getLive();
        const at = pointAt(e, frame);
        const view = viewBounds(live, viewSize.width, viewSize.height);
        const inView = at.x >= view.minX && at.x <= view.maxX && at.y >= view.minY && at.y <= view.maxY;
        const center = { x: (view.minX + view.maxX) / 2, y: (view.minY + view.maxY) / 2 };
        press.current = {
            pointerId: e.pointerId,
            x: e.clientX,
            y: e.clientY,
            moved: false,
            nodeId: (e.target as Element).getAttribute('data-map-node') ?? undefined,
            // Grabbing the view box moves it from where it was held, instead of jumping
            grab: inView ? { dx: at.x - center.x, dy: at.y - center.y } : { dx: 0, dy: 0 },
            frame,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        const p = press.current;
        if (!p || p.pointerId !== e.pointerId) return;
        if (!p.moved && Math.hypot(e.clientX - p.x, e.clientY - p.y) < CLICK_SLOP) return;
        if (!p.moved) {
            p.moved = true;
            setFrozen(p.frame);
        }
        const at = pointAt(e, p.frame);
        onMove(centeredOn(at.x - p.grab.dx, at.y - p.grab.dy, getLive().scale, viewSize.width, viewSize.height), false);
    };

    const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
        const p = press.current;
        if (!p || p.pointerId !== e.pointerId) return;
        press.current = null;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
        if (p.moved) {
            onMove(getLive(), true);
            setFrozen(null);
            return;
        }
        const live = getLive();
        const node = p.nodeId ? nodes.find(n => n.id === p.nodeId) : undefined;
        if (node) {
            // To the node, close enough to work on but with all of it in view
            const r = nodeRect(node);
            const fits = Math.min((viewSize.width - 160) / r.width, (viewSize.height - 160) / r.height);
            const scale = Math.max(0.1, Math.min(Math.max(live.scale, 0.75), fits, 1));
            onGlide(centeredOn(r.x + r.width / 2, r.y + r.height / 2, scale, viewSize.width, viewSize.height));
            return;
        }
        const at = pointAt(e, p.frame);
        onGlide(centeredOn(at.x, at.y, live.scale, viewSize.width, viewSize.height));
    };

    return (
        <div
            className="ns-glass absolute bottom-4 z-50 rounded-2xl p-1.5 group/map transition-[right] duration-200"
            style={{ right: MAP_RIGHT }}
            onPointerDown={(e) => e.stopPropagation()}
            data-canvas-minimap
        >
            <div className="relative rounded-xl overflow-hidden">
                <svg
                    width={MAP_WIDTH}
                    height={MAP_HEIGHT}
                    viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
                    className="block bg-cream cursor-pointer touch-none"
                    role="img"
                    aria-label={`Workspace map: ${nodes.length} node${nodes.length === 1 ? '' : 's'}. Click to go there, drag to move the view.`}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                >
                    {nodes.map((node) => {
                        const r = nodeRect(node);
                        const a = toMap(frame, r.x, r.y);
                        const selected = selectedIds.has(node.id);
                        return (
                            <rect
                                key={node.id}
                                data-map-node={node.id}
                                x={a.x}
                                y={a.y}
                                width={Math.max(2, r.width * frame.scale)}
                                height={Math.max(2, r.height * frame.scale)}
                                rx={1.5}
                                fill={TONE_COLORS[nodeTone(node.type)].solid}
                                stroke="currentColor"
                                className={selected ? 'text-lavender-deep' : 'text-ink/35'}
                                strokeWidth={selected ? 1.5 : 0.75}
                            >
                                <title>{node.data?.title || node.type}</title>
                            </rect>
                        );
                    })}
                </svg>
                {/* What is on screen */}
                <div
                    ref={viewBoxRef}
                    data-map-view
                    className="absolute top-0 left-0 pointer-events-none border-ink bg-lavender/[0.18]"
                    style={{
                        borderWidth: VIEW_BOX_LINE,
                        borderRadius: 2 + VIEW_BOX_LINE / 2,
                        willChange: 'transform',
                    }}
                />
            </div>
            <button
                type="button"
                onClick={onHide}
                className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-line absolute top-2.5 right-2.5 opacity-0 group-hover/map:opacity-100 focus-visible:opacity-100 transition-opacity"
                aria-label="Hide the map"
                title="Hide the map"
            >
                <X />
            </button>
        </div>
    );
};

export default CanvasMinimap;
